<template>
  <eco-labeled-control
    :context="context"
    :attributes="attributes"
    :direction="'column'"
  >
    <div class="eco-soc-configuration-control">
      <!-- visualization -->
      <div class="visualization">
        <div class="battery-plus-pole"></div>
        <div class="battery-body">
          <div class="battery-segment grid-feed-optimized-capacity" v-if="showGridFeedOptimizedCapacity" :title="gridFeedOptimizedCapacityLabel" :style="gridFeedOptimizedCapacityStyle"></div>
          <div class="battery-segment self-consumption-capacity" :title="selfConsumptionCapacityLabel" :style="selfConsumptionCapacityStyle"></div>
          <div class="battery-segment peak-shaving-capacity" :title="peakShavingCapacityLabel" :style="peakShavingCapacityStyle"></div>
          <div class="battery-segment load-management-capacity" :title="loadManagementCapacityLabel" :style="loadManagementCapacityStyle"></div>
          <div class="battery-segment off-grid-capacity" :title="offGridCapacityLabel" :style="offGridCapacityStyle"></div>
        </div>
      </div>
      <!-- inputs -->
      <div class="inputs">
        <div class="with-label" v-if="showGridFeedOptimizedCapacity">
          <div class="label">
            <div class="label-text">{{ gridFeedOptimizedCapacityLabel }}</div>
          </div>
          <eco-number-input class="input"
            :current-value="currentGridFeedOptimizedCapacityValue"
            :pending-value="pendingGridFeedOptimizedCapacityValue"
            :min="0"
            :max="maxGridFeedOptimizedCapacityValue"
            :step="1"
            unit="%"
            :readonly="readonly"
            @changed="onGridFeedOptimizedCapacityChanged"
          />
        </div>
        <div class="with-label">
          <div class="label">
            <div class="label-text">{{ selfConsumptionCapacityLabel }}</div>
          </div>
          <eco-number-input class="input"
            :current-value="currentSelfConsumptionCapacityValue"
            :pending-value="pendingSelfConsumptionCapacityValue"
            :min="0"
            :max="100"
            :step="1"
            unit="%"
            :readonly="true"
          />
        </div>
        <div class="with-label">
          <div class="label">
            <div class="label-text">{{ peakShavingCapacityLabel }}</div>
          </div>
          <eco-number-input class="input"
            :current-value="currentPeakShavingCapacityValue"
            :pending-value="pendingPeakShavingCapacityValue"
            :min="0"
            :max="maxPeakShavingCapacityValue"
            :step="1"
            unit="%"
            :readonly="readonly"
            @changed="onPeakShavingCapacityChanged"
          />
        </div>
        <div class="with-label">
          <div class="label">
            <div class="label-text">{{ loadManagementCapacityLabel }}</div>
          </div>
          <eco-number-input class="input"
            :current-value="currentLoadManagementCapacityValue"
            :pending-value="pendingLoadManagementCapacityValue"
            :min="0"
            :max="maxLoadManagementCapacityValue"
            :step="1"
            unit="%"
            :readonly="readonly"
            @changed="onLoadManagementCapacityChanged"
          />
        </div>
        <div class="with-label off-grid">
          <div class="label">
            <div class="label-text">{{ offGridCapacityLabel }}</div>
          </div>
          <eco-number-input class="input"
            :current-value="currentOffGridCapacityValue"
            :pending-value="pendingOffGridCapacityValue"
            :min="0"
            :max="maxOffGridCapacityValue"
            :step="1"
            unit="%"
            :readonly="readonly"
            @changed="onOffGridCapacityChanged"
          />
        </div>
      </div>
    </div>
  </eco-labeled-control>
</template>

<script lang="ts">
import { Component, Prop, Vue } from 'vue-property-decorator'
import { ControlContextV2, StateOfChargeConfigurationControlAttributes } from './models'

import EcoLabeledControl from './shared/EcoLabeledControl.vue'
import EcoNumberInput from './shared/EcoNumberInput.vue'
 
@Component({
  components: {
    EcoLabeledControl,
    EcoNumberInput,
  },
})
export default class EcoStateOfChargeConfigurationControl extends Vue {
  @Prop() public id: string
  @Prop() public context: ControlContextV2
  @Prop() public attributes: StateOfChargeConfigurationControlAttributes
  @Prop() public state: object
  @Prop() public pendingState: object
  @Prop() public readonly: boolean

  public get showGridFeedOptimizedCapacity() {
    return !!this.attributes.gridFeedOptimizedCapacity && this.state[this.attributes.gridFeedOptimizedCapacity.state] !== undefined
  }

  public get currentGridFeedOptimizedCapacityValue()  {
    return this.attributes.gridFeedOptimizedCapacity?.state ? this.state[this.attributes.gridFeedOptimizedCapacity.state] : 0
  }

  public get currentSelfConsumptionCapacityValue()  {
    return this.state[this.attributes.selfConsumptionCapacity.state]
  }

  public get currentPeakShavingCapacityValue() {
    return this.state[this.attributes.peakShavingCapacity.state]
  }

  public get currentLoadManagementCapacityValue() {
    return this.state[this.attributes.loadManagementCapacity.state]
  }

  public get currentOffGridCapacityValue() {
    return this.state[this.attributes.offGridCapacity.state]
  }

  public get pendingGridFeedOptimizedCapacityValue()  {
    return this.attributes.gridFeedOptimizedCapacity?.state ? this.pendingState[this.attributes.gridFeedOptimizedCapacity.state] : undefined
  }

  public get pendingSelfConsumptionCapacityValue()  {
    if (this.pendingGridFeedOptimizedCapacityValue || this.pendingPeakShavingCapacityValue || this.pendingLoadManagementCapacityValue || this.pendingOffGridCapacityValue) {
      const pendingGridFeedOptimizedCapacityValue = this.pendingGridFeedOptimizedCapacityValue ?? this.currentGridFeedOptimizedCapacityValue
      const pendingPeakShavingCapacityValue = this.pendingPeakShavingCapacityValue ?? this.currentPeakShavingCapacityValue
      const pendingLoadManagementCapacityValue = this.pendingLoadManagementCapacityValue ?? this.currentLoadManagementCapacityValue
      const pendingOffgridCapacityValue = this.pendingOffGridCapacityValue ?? this.currentOffGridCapacityValue
      return 100 - (pendingGridFeedOptimizedCapacityValue + pendingLoadManagementCapacityValue + pendingPeakShavingCapacityValue + pendingOffgridCapacityValue)
    } else {
      return undefined
    }
  }

  public get pendingPeakShavingCapacityValue()  {
    return this.pendingState[this.attributes.peakShavingCapacity.state]
  }

  public get pendingLoadManagementCapacityValue()  {
    return this.pendingState[this.attributes.loadManagementCapacity.state]
  }

  public get pendingOffGridCapacityValue() {
    return this.pendingState[this.attributes.offGridCapacity.state]
  }

  public get maxGridFeedOptimizedCapacityValue()  {
    return this.currentGridFeedOptimizedCapacityValue + this.currentSelfConsumptionCapacityValue
  }

  public get maxPeakShavingCapacityValue() {
    return this.currentPeakShavingCapacityValue + this.currentSelfConsumptionCapacityValue
  }

  public get maxLoadManagementCapacityValue() {
    return this.currentLoadManagementCapacityValue + this.currentSelfConsumptionCapacityValue
  }

  public get maxOffGridCapacityValue()  {
    return this.currentOffGridCapacityValue + this.currentSelfConsumptionCapacityValue
  }

  public get gridFeedOptimizedCapacityLabel()  {
    return this.attributes.gridFeedOptimizedCapacity?.label ? this.context.resolveStringResource(this.attributes.gridFeedOptimizedCapacity.label) : ''
  }

  public get selfConsumptionCapacityLabel()  {
    return this.context.resolveStringResource(this.attributes.selfConsumptionCapacity.label)
  }

  public get peakShavingCapacityLabel()  {
    return this.context.resolveStringResource(this.attributes.peakShavingCapacity.label)
  }

  public get loadManagementCapacityLabel()  {
    return this.context.resolveStringResource(this.attributes.loadManagementCapacity.label)
  }

  public get offGridCapacityLabel() {
    return this.context.resolveStringResource(this.attributes.offGridCapacity.label)
  }

  public get gridFeedOptimizedCapacityStyle() {
    return {
      height: `${this.currentGridFeedOptimizedCapacityValue}%`,
    }
  }

  public get selfConsumptionCapacityStyle() {
    return {
      height: `${this.currentSelfConsumptionCapacityValue}%`,
    }
  }

  public get peakShavingCapacityStyle() {
    return {
      height: `${this.currentPeakShavingCapacityValue}%`,
    }
  }

  public get loadManagementCapacityStyle() {
    return {
      height: `${this.currentLoadManagementCapacityValue}%`,
    }
  }

  public get offGridCapacityStyle() {
    return {
      height: `${this.currentOffGridCapacityValue}%`,
    }
  }

  public onGridFeedOptimizedCapacityChanged(value: number) {
    if (!this.attributes.gridFeedOptimizedCapacity?.command || !this.attributes.gridFeedOptimizedCapacity?.state) {
      return
    }
    
    this.context.executeCommand(this.id, {
      command: this.attributes.gridFeedOptimizedCapacity.command,
      params: value,
      pendingState: {
        [this.attributes.gridFeedOptimizedCapacity.state]: value,
      },
    })
  }

  public onPeakShavingCapacityChanged(value: number) {
    this.context.executeCommand(this.id, {
      command: this.attributes.peakShavingCapacity.command,
      params: value,
      pendingState: {
        [this.attributes.peakShavingCapacity.state]: value,
      },
    })
  }

  public onLoadManagementCapacityChanged(value: number) {
    this.context.executeCommand(this.id, {
      command: this.attributes.loadManagementCapacity.command,
      params: value,
      pendingState: {
        [this.attributes.loadManagementCapacity.state]: value,
      },
    })
  }

  public onOffGridCapacityChanged(value: number) {
    this.context.executeCommand(this.id, {
      command: this.attributes.offGridCapacity.command,
      params: value,
      pendingState: {
        [this.attributes.offGridCapacity.state]: value,
      },
    })
  }
}
</script>

<style lang="scss" scoped>
.eco-soc-configuration-control {
  display: flex;
  justify-content: space-between;
  .visualization {
    display: flex;
    flex-direction: column;
    align-items: center;
    margin-top: 12px;
    margin-bottom: 12px;
    .battery-plus-pole {
      height: 5%;
      width: 20px;
      background: white;
      border: solid 1px white;
    }
    .battery-body {
      height: 95%;
      display: flex;
      flex-direction: column;
      border: solid 1px white;
      border-radius: 5px;
      flex: 1;
      .battery-segment {
        width: 60px;
      }
      .grid-feed-optimized-capacity {
        background: linear-gradient(to right, lime, lightgreen);
        border-radius: 5px 5px 0px 0px;
      }
      .self-consumption-capacity {
        background: linear-gradient(to right, green, darkgreen);
      }
      .peak-shaving-capacity {
        background: linear-gradient(to right, yellow,orange);
      }
      .load-management-capacity {
        background: linear-gradient(to right, blue, darkblue);
      }
      .off-grid-capacity {
        background: linear-gradient(to right, #fa0f36,#78022d);
        border-radius: 0px 0px 5px 5px;
      }
    }
  }
  .inputs {
    display: flex;
    flex-direction: column;
    .with-label {
      height: 35px;
      display: flex;
      justify-content: center;
      align-content: center;
      justify-items: center;
      align-content: center;
      .label {
        display: flex;
        justify-content: center;
        align-items: center;
        margin: 0px 10px;
      }
      .off-grid {
        input {
          max-width: 50px;
        }
      }
    }
  }
} 
</style>

<style lang="css">
</style>
